/** function.path を /xxx 形式に正規化（前後空白・末尾スラッシュを除去） */
export function normalizeFunctionPath(path) {
    if (path == null || path === "") return null;
    let p = String(path).trim();
    if (!p) return null;
    if (!p.startsWith("/")) p = `/${p}`;
    if (p.length > 1) p = p.replace(/\/+$/, "");
    return p;
}

/**
 * master-api の m_menu_group_function_list レスポンスを sidebar 用行に変換
 */
export function normalizeMenuRowsFromMasterApi(listRows) {
    if (!Array.isArray(listRows) || listRows.length === 0) return [];

    const byGroupId = new Map();
    for (const r of listRows) {
        const gid = r.menu_group_id;
        if (gid == null) continue;
        if (!byGroupId.has(gid)) {
            byGroupId.set(gid, {
                menu_group_id: gid,
                menu_group_name: r.menu_group_name ?? "",
                icon_id: r.icon_id ?? null,
                icon_color: r.icon_color ?? null,
                label_color: r.label_color ?? null,
                disp_seq: r.disp_seq ?? null,
                parent_menu_id: r.parent_menu_id ?? null,
                functions: [],
            });
        }
        const g = byGroupId.get(gid);
        if (r.function_id != null) {
            g.functions.push({
                function_id: r.function_id,
                function_name: r.function_name ?? "",
                icon_id: r.function_icon_id ?? null,
                icon_color: r.function_icon_color ?? null,
                label_color: r.function_label_color ?? null,
                path: r.function_path ?? null,
                disp_seq: r.function_disp_seq ?? null,
            });
        }
    }

    const groups = [...byGroupId.values()];
    groups.sort((a, b) => {
        const da = a.disp_seq == null ? Number.POSITIVE_INFINITY : Number(a.disp_seq);
        const db = b.disp_seq == null ? Number.POSITIVE_INFINITY : Number(b.disp_seq);
        if (da !== db) return da - db;
        return Number(a.menu_group_id) - Number(b.menu_group_id);
    });
    groups.forEach((g) => {
        g.functions.sort((a, b) => {
            const da = a.disp_seq == null ? Number.POSITIVE_INFINITY : Number(a.disp_seq);
            const db = b.disp_seq == null ? Number.POSITIVE_INFINITY : Number(b.disp_seq);
            if (da !== db) return da - db;
            return Number(a.function_id) - Number(b.function_id);
        });
    });

    const out = [];
    for (const g of groups) {
        const groupName = g.menu_group_name ?? "";
        const isStandalone =
            g.parent_menu_id != null && String(g.parent_menu_id) === String(g.menu_group_id);
        if (isStandalone) {
            const first = g.functions[0];
            if (first?.path) {
                out.push({
                    parent_menu_group_name: groupName,
                    menu_group_name: groupName,
                    icon_id: g.icon_id,
                    icon_color: g.icon_color,
                    label_color: g.label_color,
                    path: first.path,
                    function_id: first.function_id,
                    disp_seq: g.disp_seq,
                });
            }
            continue;
        }

        out.push({
            parent_menu_group_name: groupName,
            menu_group_name: groupName,
            icon_id: g.icon_id,
            icon_color: g.icon_color,
            label_color: g.label_color,
            path: null,
            function_id: null,
            disp_seq: g.disp_seq,
        });

        for (const f of g.functions) {
            if (!f.path) continue;
            out.push({
                parent_menu_group_name: groupName,
                menu_group_name: f.function_name ?? "",
                icon_id: f.icon_id,
                icon_color: f.icon_color,
                label_color: f.label_color,
                path: f.path,
                function_id: f.function_id,
                disp_seq: f.disp_seq,
            });
        }
    }

    return out;
}

/**
 * APIのメニュー行をセクションと「セクションなし機能項目」に分けて順序付きで返す
 */
export function buildMenuSections(menuRows) {
    if (!Array.isArray(menuRows) || menuRows.length === 0) return [];
    const blocks = [];
    const pathNorm = normalizeFunctionPath;

    for (const row of menuRows) {
        const parent = row.parent_menu_group_name ?? "";
        const name = row.menu_group_name ?? "";
        const hasPath = row.path != null && row.path !== "";

        if (!hasPath) {
            blocks.push({
                type: "section",
                sectionKey: parent,
                sectionName: name,
                sectionIconId: row.icon_id ?? null,
                sectionIconColor: row.icon_color ?? null,
                sectionLabelColor: row.label_color ?? null,
                items: [],
            });
            continue;
        }

        if (parent === name) {
            blocks.push({
                type: "standalone",
                item: {
                    menu_group_name: name,
                    icon_id: row.icon_id ?? null,
                    icon_color: row.icon_color ?? null,
                    label_color: row.label_color ?? null,
                    path: pathNorm(row.path),
                    function_id: row.function_id ?? null,
                },
            });
            continue;
        }

        const lastSection = [...blocks].reverse().find((b) => b.type === "section" && b.sectionKey === parent);
        if (lastSection) {
            lastSection.items.push({
                menu_group_name: name,
                icon_id: row.icon_id ?? null,
                icon_color: row.icon_color ?? null,
                label_color: row.label_color ?? null,
                path: pathNorm(row.path),
                function_id: row.function_id ?? null,
            });
        }
    }

    return blocks;
}

/** メニュー行から許可 function path の Set を生成 */
export function collectAllowedPaths(menuRows) {
    const paths = new Set();
    for (const row of menuRows || []) {
        const p = normalizeFunctionPath(row.path);
        if (p) paths.add(p);
    }
    return paths;
}

/**
 * 親メニュー path が許可されていれば子 path も許可する（ドリルダウン先など）
 * MenuRouteGuard 経由のアクセス判定で使用
 */
const MENU_CHILD_PATHS_BY_PARENT = {
    "/consignment-status": ["/consignment-active-check", "/consignment-sales-analysis"],
    "/consignment-reports": ["/consignment-sales-analysis"],
    "/event-order": ["/event-order-ranking"],
    "/event-attendee-registration": ["/event-customer-summary"],
};

/** /dashboard 配下の pathname がメニュー許可 path か判定（ダッシュボード本体は常に許可） */
export function isMenuPathAllowed(pathname, allowedPaths) {
    const relative = (pathname || "").replace(/^\/dashboard\/?/, "/") || "/";
    if (relative === "/" || relative === "") return true;

    const normalized = normalizeFunctionPath(relative);
    if (!normalized || normalized === "/") return true;

    // ベルアイコン・お知らせ画面（メニュー未登録でも全ログインユーザーが利用可能）
    if (normalized === "/announcements" || normalized.startsWith("/announcements/")) {
        return true;
    }
    if (normalized === "/announcement-admin" || normalized.startsWith("/announcement-admin/")) {
        return true;
    }
    if (normalized === "/line-message" || normalized.startsWith("/line-message/")) {
        return true;
    }

    for (const allowed of allowedPaths) {
        if (normalized === allowed || normalized.startsWith(`${allowed}/`)) {
            return true;
        }
    }

    for (const [parentPath, childPaths] of Object.entries(MENU_CHILD_PATHS_BY_PARENT)) {
        if (!allowedPaths.has(parentPath)) continue;
        for (const childPath of childPaths) {
            if (normalized === childPath || normalized.startsWith(`${childPath}/`)) {
                return true;
            }
        }
    }

    return false;
}
